import type {
  GridApi,
  GridCellParams,
  GridCellSelectionModel,
  GridColDef,
  GridRowSelectionModel,
  GridSingleSelectColDef,
} from '@repo/go-table';
import { getUnixTime, isDate } from 'date-fns';
import i18nextInstance from 'i18next';
import { action, computed, makeObservable, observable } from 'mobx';
import type { MutableRefObject } from 'react';

import { convertExcelStringToValueArrays } from '../../shared/convertExcelStringToValueArrays';
import type { INotificationsService } from '../../shared/INotificationsService';

import { RECOMMENDATION_MULTILINE_TEXT_FIELDS, type TRecommendationMultilineTextField } from './constants';
import type { Recommendation, TData } from './services/Recommendation.entity';
import { RecommendationsDisplayManager, SERVICE_COLUMNS } from './services/RecommendationsDisplayManager';
import type { RecommendationsService } from './services/RecommendationsService';
import type { TRecommendationRowData } from './types';

type SingleSelectOption = string | number | { label?: unknown; value?: unknown; [key: string]: unknown };
type TClipboardValue = string | number | boolean | Date | null;
type TPasteTarget = { id: string; field: string };

const MULTI_SELECT_FIELDS = ['blockIds', 'fieldIds', 'padIds'] as const;

type TMultiSelectField = (typeof MULTI_SELECT_FIELDS)[number];

const CLEARABLE_FIELDS = new Set<keyof TRecommendationRowData>([
  'name',
  'description',
  'blockIds',
  'fieldIds',
  'padIds',
  'indexFormat',
  'dataType',
  'paramValue',
  'recommendationMin',
  'recommendationMax',
  'recommendationDelta',
]);

export class RecommendationsTableStore {
  private tableApi: MutableRefObject<GridApi>;

  @observable displayManager: RecommendationsDisplayManager;
  @observable selectedRows: GridRowSelectionModel = [];

  @observable isTableSettingsOpen = false;
  @observable isContextPanelOpen = false;
  @observable isRowSelectionModeOn = false;

  constructor(
    tableApi: MutableRefObject<GridApi>,
    private readonly notifications: INotificationsService,
    private readonly recommendationsService: RecommendationsService,
  ) {
    this.tableApi = tableApi;
    this.displayManager = new RecommendationsDisplayManager();
    makeObservable(this);
  }

  @computed
  get isLoading(): boolean {
    return this.recommendationsService.loading;
  }

  get isEditing(): boolean {
    return this.recommendationsService.uiState.editing;
  }

  @computed
  get isRowSelectingEnabled(): boolean {
    return this.isEditing || this.isRowSelectionModeOn;
  }

  @action.bound
  private addRecommendations(rows: Recommendation[]): void {
    if (!this.tableApi || !rows.length) return;
    this.tableApi.current.updateRows(rows);

    const last = rows[rows.length - 1];
    if (last) {
      setTimeout(() => {
        this.tableApi?.current.setCellFocus(last.entityId, 'name');
        this.tableApi?.current.startCellEditMode({ id: last.entityId, field: 'name' });
      }, 100);
    }
  }

  @action.bound
  private updateRecommendations(rows: Recommendation[]): void {
    if (!this.tableApi || !rows.length) return;
    this.tableApi.current.updateRows(rows);
  }

  @action.bound
  private deleteRecommendations(ids: string[]): void {
    if (!this.tableApi || !ids.length) return;
    this.tableApi.current.updateRows(ids.map((id) => ({ entityId: id, _action: 'delete' })));
  }

  getFieldValue = <T extends keyof TRecommendationRowData>(
    rowId: string,
    field: T,
  ): TRecommendationRowData[T] | null => {
    const record = this.recommendationsService.recommendationsMap.get(rowId);
    const value = record?.data[field] ?? null;
    if (value == null) return null;
    return value as TRecommendationRowData[T];
  };

  @action.bound
  onRecommendationItemValueChange<T extends keyof TRecommendationRowData>(
    rowId: string,
    field: T,
    value: TRecommendationRowData[T] | undefined,
  ): void {
    this.recommendationsService.onRecommendationItemValueChange(rowId, field, value);
  }

  @action.bound
  setSelectedRows(model: GridRowSelectionModel): void {
    this.selectedRows = model;
    this.tableApi.current.setRowSelectionModel(model);
  }

  isCellEditable(params: GridCellParams<Recommendation>): boolean {
    return !!params.colDef.editable;
  }

  private isCellClearable(rowId: string, field: string): field is keyof TRecommendationRowData {
    const row = this.recommendationsService.recommendationsMap.get(rowId);

    if (!row || !CLEARABLE_FIELDS.has(field as keyof TRecommendationRowData)) {
      return false;
    }

    const column = this.tableApi.current.getColumn(field);

    if (!column) {
      return false;
    }

    return this.isCellEditable({ id: rowId, field, row, colDef: column } as GridCellParams<Recommendation>);
  }

  private getClearValue(field: keyof TRecommendationRowData): string | number[] | null {
    if (this.isMultiSelectField(field)) {
      return [];
    }

    if (
      field === 'description' ||
      field === 'recommendationMin' ||
      field === 'recommendationMax' ||
      field === 'recommendationDelta'
    ) {
      return '';
    }

    return null;
  }

  @action.bound
  handleDelete(params: GridCellParams<Recommendation>): void {
    if (!this.isEditing) {
      return;
    }

    const cellSelection = this.tableApi.current.getCellSelectionModel();
    const targetCells =
      cellSelection && Object.keys(cellSelection).length
        ? cellSelection
        : {
            [params.id]: {
              [params.field]: true,
            },
          };
    const patches: [string, Partial<TData>][] = [];

    for (const rowId in targetCells) {
      const patch: Partial<TData> = {};

      for (const field in targetCells[rowId]) {
        if (!targetCells[rowId][field]) {
          continue;
        }

        if (this.isCellClearable(rowId, field)) {
          (patch as Record<string, unknown>)[field] = this.getClearValue(field);
        }
      }

      if (Object.keys(patch).length) {
        patches.push([rowId, patch]);
      }
    }

    if (patches.length) {
      this.recommendationsService.applyRecommendationItemPatches(patches);
    }
  }

  @action.bound
  setIsTableSettingsOpen(is: boolean): void {
    this.isTableSettingsOpen = is;
  }

  @action.bound
  setIsContextPanelOpen(is: boolean): void {
    this.isContextPanelOpen = is;
  }

  @action.bound
  setIsRowSelectionModeOn(is: boolean): void {
    this.isRowSelectionModeOn = is;
  }

  private isMultiSelectField(field: string): field is TMultiSelectField {
    return MULTI_SELECT_FIELDS.includes(field as TMultiSelectField);
  }

  private isMultilineTextField(field: string): field is TRecommendationMultilineTextField {
    return RECOMMENDATION_MULTILINE_TEXT_FIELDS.includes(field as TRecommendationMultilineTextField);
  }

  private getPasteTarget(
    pasteTarget: TPasteTarget | undefined,
    focusedCellData: { id: unknown; field: string } | null | undefined,
  ): TPasteTarget | null {
    if (pasteTarget) {
      return pasteTarget;
    }

    return focusedCellData ? { id: String(focusedCellData.id), field: focusedCellData.field } : null;
  }

  private isSingleValuePerClipboardRow(rows: TClipboardValue[][]): boolean {
    return rows.every((row) => row.filter((value) => value !== null && value !== '').length <= 1);
  }

  private shouldPasteAsMultilineTextCell(clipboardData: string, rows: TClipboardValue[][]): boolean {
    return /[\r\n]/.test(clipboardData) && rows.length > 1 && this.isSingleValuePerClipboardRow(rows);
  }

  private findSingleSelectOption(options: SingleSelectOption[], columnValue: string | number | boolean | Date) {
    const normalizedValue = columnValue.toString().toLowerCase();
    const matchedOptions = options.filter((option) => {
      if (typeof option === 'string' || typeof option === 'number') {
        return option.toString().toLowerCase() === normalizedValue;
      }

      return (
        option.label?.toString().toLowerCase() === normalizedValue ||
        option.value?.toString().toLowerCase() === normalizedValue
      );
    });

    return matchedOptions.length === 1 ? matchedOptions[0] : null;
  }

  private parseMultiSelectValue(
    options: SingleSelectOption[],
    columnValue: string | number | boolean | Date,
  ): number[] | null {
    const values = columnValue
      .toString()
      .split(',')
      .map((value) => value.trim())
      .filter(Boolean);

    const parsedValues: number[] = [];

    for (const value of values) {
      const option = this.findSingleSelectOption(options, value);

      if (option == null) {
        return null;
      }

      if (typeof option === 'number') {
        parsedValues.push(option);
        continue;
      }

      if (typeof option !== 'string' && typeof option.value === 'number') {
        parsedValues.push(option.value);
        continue;
      }

      return null;
    }

    return parsedValues;
  }

  private parsePasteDataToRowData(
    columns: GridColDef[],
    rows: TClipboardValue[][],
    existingRowIds: string[] = [],
  ): TData[] {
    return columns.reduce((acc: TData[], col, columnIndex) => {
      rows.forEach((row, rowIndex) => {
        if (columnIndex >= row.length) return;

        const data = { ...(acc[rowIndex] ?? {}) };
        const existingRowId = existingRowIds[rowIndex];
        const existingRecommendation = existingRowId
          ? this.recommendationsService.recommendationsMap.get(existingRowId)
          : null;
        const rowData = { ...(existingRecommendation?.data ?? {}), ...data };
        const columnValue = row[columnIndex];

        if (columnValue === null) {
          data[col.field] = null;
        } else {
          switch (col.type) {
            case 'singleSelect': {
              const optionsRaw = (col as GridSingleSelectColDef).valueOptions;
              const options: SingleSelectOption[] = Array.isArray(optionsRaw)
                ? optionsRaw
                : (optionsRaw?.({ field: col.field, row: { data: rowData } }) ?? []);

              if (this.isMultiSelectField(col.field)) {
                const value = this.parseMultiSelectValue(options, columnValue);

                if (value != null) {
                  data[col.field] = value;
                }

                break;
              }

              const value = this.findSingleSelectOption(options, columnValue);

              if (value != null) {
                if (typeof value === 'string' || typeof value === 'number') {
                  data[col.field] = value;
                } else if (typeof value.value === 'string' || typeof value.value === 'number') {
                  data[col.field] = value.value;
                }
              }
              break;
            }
            case 'date':
            case 'dateTime': {
              if (isDate(columnValue)) data[col.field] = getUnixTime(columnValue as Date);
              break;
            }
            case 'string': {
              data[col.field] = typeof columnValue === 'string' ? columnValue : columnValue.toString();
              break;
            }
            case 'boolean': {
              data[col.field] = typeof columnValue === 'boolean' ? columnValue : !!columnValue;
              break;
            }
            case 'number': {
              if (typeof columnValue === 'number') {
                data[col.field] = columnValue;
              } else if (columnValue == null) {
                data[col.field] = null;
              } else {
                const n = Number(columnValue);
                data[col.field] = Number.isNaN(n) ? null : n;
              }
              break;
            }
            default: {
              data[col.field] = columnValue as unknown;
            }
          }
        }

        acc[rowIndex] = data;
      });
      return acc;
    }, []);
  }

  private copySelectedRowsToClipboard = async () => {
    const api = this.tableApi?.current;
    if (!api) return;

    const rowsVisibility = api.state.filter.filteredRowsLookup;
    const columnsVisibility = api.state.columns.columnVisibilityModel;

    const sortedRows = api.getSortedRows() as Array<{ entityId?: string; id?: string }>;
    const selectedRows = api.getSelectedRows();
    const filteredRows = sortedRows.filter((row) => {
      const id = row.entityId ?? row.id;
      return id != null && selectedRows.get(id) && rowsVisibility[id];
    });
    const columns = api.getAllColumns();

    const copyData = filteredRows
      .map((row) => {
        const id = row.entityId ?? row.id;
        if (!id) return '';
        const values: string[] = [];
        for (const { field } of columns) {
          if (columnsVisibility[field] === false || SERVICE_COLUMNS.includes(field) || field === '__check__') continue;
          const cellParams = api.getCellParams(id, field);
          values.push(
            this.formatClipboardCellValue(
              cellParams?.formattedValue?.toString() ?? cellParams?.value?.toString() ?? '-',
            ),
          );
        }
        return values.join('\t');
      })
      .filter(Boolean)
      .join('\n');

    try {
      await navigator.clipboard.writeText(copyData);
      this.setSelectedRows([]);
      this.notifications.showSuccessMessageT('alertsRecommendations:dataCopied');
    } catch (e) {
      console.error(e);
      this.notifications.showErrorMessageT('alertsRecommendations:errors.blockedCopyPaste');
    }
  };

  private copySelectedCellData = async (selectedCellData: GridCellSelectionModel) => {
    const api = this.tableApi?.current;
    if (!api) return;

    const dataRows: string[] = [];
    for (const rowId in selectedCellData) {
      const values: string[] = [];
      for (const fieldId in selectedCellData[rowId]) {
        const cellParams = api.getCellParams(rowId, fieldId);
        values.push(
          this.formatClipboardCellValue(cellParams?.formattedValue?.toString() ?? cellParams?.value?.toString() ?? '-'),
        );
      }
      dataRows.push(values.join('\t'));
    }

    const copyData = dataRows.join('\n');
    try {
      await navigator.clipboard.writeText(copyData);
      api.setRowSelectionModel([]);
      this.notifications.showSuccessMessageT('alertsRecommendations:dataCopied');
    } catch (e) {
      console.error(e);
      this.notifications.showErrorMessageT('alertsRecommendations:errors.blockedCopyPaste');
    }
  };

  @action.bound
  handleCopy() {
    const api = this.tableApi;
    if (!api) return;

    const selectedRows = api.current.getSelectedRows();

    if (selectedRows.size < 1) {
      const cellSelection = api.current.getCellSelectionModel();
      const focusedCellData = api.current.state.focus.cell;

      const target = ((): GridCellSelectionModel | null => {
        if (cellSelection && !!Object.keys(cellSelection).length) return cellSelection;
        if (focusedCellData) {
          return { [focusedCellData.id]: { [focusedCellData.field]: true } };
        }
        return null;
      })();

      if (!target) return;
      this.copySelectedCellData(target);
      return;
    }

    this.copySelectedRowsToClipboard();
  }

  private formatClipboardCellValue(value: string): string {
    if (!/[\t\n"]/.test(value)) {
      return value;
    }

    return `"${value.replaceAll('"', '""')}"`;
  }

  @action.bound
  async handlePaste(pasteTarget?: TPasteTarget): Promise<void> {
    const api = this.tableApi?.current;
    if (!api || !this.isEditing) return;

    const target = this.getPasteTarget(pasteTarget, api.state.focus.cell);

    let clipboardData = '';
    try {
      clipboardData = await navigator.clipboard.readText();
    } catch (e) {
      console.error(e);
      this.notifications.showErrorMessageT('alertsRecommendations:errors.blockedCopyPaste');
      return;
    }

    if (!clipboardData || typeof clipboardData !== 'string') return;

    const columns = api
      .getVisibleColumns()
      .filter((c) => !SERVICE_COLUMNS.includes(c.field) && c.field !== '__check__');
    const initialDataRows = convertExcelStringToValueArrays(clipboardData, {
      boolean: {
        true: [i18nextInstance.t('common:yes')],
        false: [i18nextInstance.t('common:no')],
      },
    }).filter((row) => !row.every((val) => val === null));

    if (
      target &&
      this.isMultilineTextField(target.field) &&
      this.shouldPasteAsMultilineTextCell(clipboardData, initialDataRows)
    ) {
      this.recommendationsService.onRecommendationItemValueChange(
        target.id,
        target.field,
        clipboardData.replace(/\r\n/g, '\n').replace(/\r/g, '\n'),
      );
      return;
    }

    if (!target) {
      const rowData = this.parsePasteDataToRowData(columns, initialDataRows);
      this.recommendationsService.addNewRecommendationItemPatches(rowData);
      return;
    }

    const pasteTargetColumnIndex = columns.findIndex((c) => c.field === target.field);
    const allRowsIds = api.getSortedRowIds();
    const pasteTargetRowIndex = allRowsIds.indexOf(target.id);

    if (pasteTargetColumnIndex < 0 || pasteTargetRowIndex < 0) {
      return;
    }

    const existingRows = allRowsIds.slice(pasteTargetRowIndex, pasteTargetRowIndex + initialDataRows.length);
    const rowData = this.parsePasteDataToRowData(
      columns.slice(pasteTargetColumnIndex),
      initialDataRows,
      existingRows.map(String),
    );

    const updateData = rowData.slice(0, existingRows.length);
    const newData = rowData.slice(existingRows.length);

    this.recommendationsService.applyRecommendationItemPatches(
      existingRows.map((rowId, index) => [rowId as string, updateData[index]]),
    );
    this.recommendationsService.addNewRecommendationItemPatches(newData);
  }

  @action.bound
  init() {
    const newDisposer = this.recommendationsService.eventEmitter.addEventListener(
      'newRecommendations',
      this.addRecommendations,
    );
    const updateDisposer = this.recommendationsService.eventEmitter.addEventListener(
      'updateRecommendations',
      this.updateRecommendations,
    );
    const deleteDisposer = this.recommendationsService.eventEmitter.addEventListener(
      'deleteRecommendations',
      this.deleteRecommendations,
    );

    return () => {
      newDisposer();
      updateDisposer();
      deleteDisposer();
    };
  }
}
